import { GetServerSideProps, NextApiResponse } from 'next'
import type { EducatorCohortDetailsResponse } from '@mc/api'
import { ProgramDetailsPage } from '../features/ProgramDetails'
import { getServerSession } from '../utils/auth'
import { clearPendingEmailCookie } from '../utils/cookies'
import { getCohortDetailsForSeed } from '../utils/enrollmentStatus'
import { shouldRedirectToOnboarding } from '../utils/onboardingRedirect'
import { getPageTitle } from '../utils/pageTitle'
import { paths } from '../utils/paths'
import { getServerSidePropsWithProductPrice } from '../utils/pricing'
import { getProgramDetailsSectionOrder } from '../utils/sectionLayout'

interface ProgramDetailsProps {
  initialIsMobile: boolean
  cohortDetails?: EducatorCohortDetailsResponse | null
  sectionOrder?: string[] | null
}

const ProgramDetails = ({
  initialIsMobile,
  cohortDetails,
  sectionOrder,
}: ProgramDetailsProps) => (
  <ProgramDetailsPage
    initialIsMobile={initialIsMobile}
    initialCohortDetails={cohortDetails ?? undefined}
    sectionOrder={sectionOrder ?? undefined}
  />
)

ProgramDetails.pageSettings = {
  title: getPageTitle(),
  pageName: 'product-name-program-details',
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  clearPendingEmailCookie(ctx.res as NextApiResponse)

  const session = await getServerSession(ctx)

  if (session) {
    if (await shouldRedirectToOnboarding(session.accessToken)) {
      return {
        redirect: {
          destination: paths.onboarding,
          permanent: false,
        },
      }
    }

    // Redirect authenticated users to enrollment overview
    return {
      redirect: {
        destination: paths.enrollmentOverview,
        permanent: false,
      },
    }
  }

  const userAgent = ctx.req.headers['user-agent'] || ''
  const initialIsMobile = /Mobile|Android|iPhone/i.test(userAgent)

  // Seeded server-side so the hero strip, CTAs and section order don't flash on first paint.
  const [cohortDetails, sectionOrder] = await Promise.all([
    getCohortDetailsForSeed(),
    getProgramDetailsSectionOrder(),
  ])

  return getServerSidePropsWithProductPrice(async () => ({
    props: { initialIsMobile, cohortDetails, sectionOrder },
  }))(ctx)
}

export default ProgramDetails
